// "Abierto ahora": interpreta el horario que el dueño escribe a mano en el panel
// ("Lunes a viernes de 8 a 20", "Martes a domingo de 19 a 0", "Sábados cerrado"...)
// y calcula si el negocio está abierto en este momento, en hora de Argentina.
// Si algún renglón no se entiende, no se arriesga: devuelve null y el menú no muestra nada.

const DAY_NAMES = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

// Palabra (sin tildes) → día de la semana (0 = domingo, como Date.getDay).
const DAY_WORDS = {
    lunes: 1, lun: 1,
    martes: 2, mar: 2,
    miercoles: 3, mier: 3, mie: 3,
    jueves: 4, jue: 4,
    viernes: 5, vie: 5,
    sabado: 6, sabados: 6, sab: 6,
    domingo: 0, domingos: 0, dom: 0
};

const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const DAY = 24 * 60;

const normalize = (text) => String(text || "")
    .toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const TIME = String.raw`(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?\s*(?:horas|hrs|hs|h)?\.?`;
const TIME_RE = new RegExp(String.raw`(?:(?:de(?:sde)?\s+)?(?:las?\s+)?${TIME}\s*(?:a|al|-|–|hasta)\s*(?:las?\s+)?${TIME})|(?:hasta\s+(?:las?\s+)?${TIME})`, "g");
const DAY_RE = new RegExp(String.raw`\b(${Object.keys(DAY_WORDS).sort((a, b) => b.length - a.length).join("|")})\b`, "g");
const CLOSED_RE = /cerrad|no abrimos|no abre|franco/;
const ALWAYS_RE = /\b24 ?(horas|hs|h)\b|abierto siempre/;

// Hora y minutos → minutos desde las 0. null si no es una hora válida.
const toMinutes = (h, m, ampm) => {
    let hour = Number(h);
    const min = m ? Number(m) : 0;
    if (ampm === "pm" && hour < 12) hour += 12;
    if (ampm === "am" && hour === 12) hour = 0;
    if (hour === 24 && min === 0) hour = 0;
    if (hour > 23 || min > 59) return null;
    return hour * 60 + min;
}

// Los días que nombra un pedazo de texto: "lunes a viernes", "martes, jueves y sábados",
// "todos los días", "fines de semana". null si no nombra ninguno.
const parseDays = (text) => {
    if (/todos los dias|toda la semana|de corrido/.test(text)) return [...ALL_DAYS];
    const days = new Set();
    if (/fin(es)? de semana|finde/.test(text)) { days.add(6); days.add(0); }

    const found = [...text.matchAll(DAY_RE)];
    for (let i = 0; i < found.length; i++) {
        const day = DAY_WORDS[found[i][1]];
        const next = found[i + 1];
        const between = next ? text.slice(found[i].index + found[i][0].length, next.index) : "";
        if (next && /^\s*(a|al|hasta|-|–)\s*$/.test(between)) {
            // Rango: "viernes a lunes" da la vuelta por el fin de semana.
            const end = DAY_WORDS[next[1]];
            for (let d = day; ; d = (d + 1) % 7) {
                days.add(d);
                if (d === end) break;
            }
            i++;
        } else {
            days.add(day);
        }
    }
    return days.size ? [...days] : null;
}

// Un renglón del horario → lista de reglas { days, ranges } | { days, closed } | { days, until }.
// Un renglón puede tener varios grupos ("Lunes a viernes 8 a 12 y 16 a 20, sábados 9 a 13"):
// cada horario se asigna a los días nombrados justo antes (o después, si arranca con la hora).
// Los rangos son [apertura, cierre] en minutos; si el cierre es menor o igual, cierra
// pasada la medianoche. null si algo del renglón no se entiende.
export const parseHoursLine = (line) => {
    const text = normalize(line);
    if (!text) return null;

    const times = [...text.matchAll(TIME_RE)];
    const chunks = [];
    let pos = 0;
    for (const m of times) {
        chunks.push(text.slice(pos, m.index));
        pos = m.index + m[0].length;
    }
    chunks.push(text.slice(pos));

    // Números sueltos que no forman un horario ("8 y 30", "de 8 20"): no adivinamos.
    if (chunks.some((c) => /\d/.test(c.replace(ALWAYS_RE, "")))) return null;

    const groups = chunks.map(parseDays);
    const used = groups.map(() => false);
    const rules = [];
    let current = null;

    times.forEach((m, i) => {
        if (groups[i]) { current = groups[i]; used[i] = true; }
        if (!current) {
            // Hora primero: "8 a 20 de lunes a viernes".
            const ahead = groups.findIndex((g, j) => j > i && g);
            if (ahead !== -1) { current = groups[ahead]; used[ahead] = true; }
            else current = [...ALL_DAYS];
        }
        if (m[1] !== undefined) {
            const start = toMinutes(m[1], m[2], m[3]);
            const end = toMinutes(m[4], m[5], m[6]);
            if (start === null || end === null || start === end) { rules.push(null); return; }
            rules.push({ days: current, ranges: [[start, end]] });
        } else {
            const until = toMinutes(m[7], m[8], m[9]);
            if (until === null) { rules.push(null); return; }
            rules.push({ days: current, until });
        }
    });
    if (rules.includes(null)) return null;

    // Días nombrados sin hora: tienen que decir "cerrado" o "24 horas".
    for (let i = 0; i < chunks.length; i++) {
        if (used[i]) continue;
        const always = ALWAYS_RE.test(chunks[i]);
        if (groups[i]) {
            if (always) rules.push({ days: groups[i], ranges: [[0, DAY]] });
            else if (CLOSED_RE.test(chunks[i])) rules.push({ days: groups[i], closed: true });
            else return null;
        } else if (always && !groups.some(Boolean)) {
            rules.push({ days: [...ALL_DAYS], ranges: [[0, DAY]] });
        }
    }
    return rules.length ? rules : null;
}

// Los dos renglones del panel → horario de la semana: un arreglo de 7 días
// (0 = domingo) con los rangos de cada uno. El segundo renglón pisa al primero
// en los días que nombra ("Viernes y sábados hasta la 1" solo corre el cierre).
// null si no se entiende alguno de los renglones cargados.
export const parseSchedule = (horario, horarioSec) => {
    const lines = [horario, horarioSec].filter((l) => normalize(l));
    if (!lines.length) return null;

    const week = ALL_DAYS.map(() => []);
    for (const line of lines) {
        const rules = parseHoursLine(line);
        if (!rules) return null;
        const touched = new Set();
        for (const rule of rules) {
            for (const d of rule.days) {
                if (rule.ranges) {
                    // Dentro del mismo renglón los turnos se suman; de un renglón a otro, se pisan.
                    if (!touched.has(d)) week[d] = [];
                    touched.add(d);
                    week[d].push(...rule.ranges.map((r) => [...r]));
                } else if (rule.closed) {
                    week[d] = [];
                    touched.add(d);
                } else {
                    if (!week[d].length) return null;
                    week[d][week[d].length - 1][1] = rule.until;
                }
            }
        }
    }
    return week;
}

// Fecha → día de la semana y minutos en hora de Argentina (UTC−3, sin horario de verano).
export const argentinaClock = (date = new Date()) => {
    const ar = new Date(date.getTime() - 3 * 60 * 60 * 1000);
    return { day: ar.getUTCDay(), minutes: ar.getUTCHours() * 60 + ar.getUTCMinutes() };
}

const formatTime = (minutes) => {
    const m = minutes % (24 * 60);
    const h = Math.floor(m / 60);
    const min = m % 60;
    return min ? `${h}:${String(min).padStart(2, "0")}` : String(h);
}

const at = (minutes) => (Math.floor(minutes % (24 * 60) / 60) === 1 ? "a la " : "a las ") + formatTime(minutes);

// Inglés y portugués para el menú traducido (src/i18n.js). En inglés va con minutos ("7:00 pm").
const enTime = (minutes) => {
    const m = minutes % (24 * 60);
    const h = Math.floor(m / 60);
    return `${h % 12 || 12}:${String(m % 60).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
}
const STATUS_TEXTS = {
    es: {
        open: "Abierto ahora", closed: "Cerrado", always: "Abierto las 24 horas",
        closes: (m) => "Cierra " + at(m),
        opens: (offset, day, m) => `Abre ${offset === 0 ? "hoy" : offset === 1 ? "mañana" : "el " + DAY_NAMES[day]} ${at(m)}`
    },
    en: {
        open: "Open now", closed: "Closed", always: "Open 24 hours",
        closes: (m) => "Closes at " + enTime(m),
        opens: (offset, day, m) => `Opens ${offset === 0 ? "today" : offset === 1 ? "tomorrow" : "on " + ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][day]} at ${enTime(m)}`
    },
    pt: {
        open: "Aberto agora", closed: "Fechado", always: "Aberto 24 horas",
        closes: (m) => "Fecha às " + formatTime(m) + "h",
        opens: (offset, day, m) => `Abre ${offset === 0 ? "hoje" : offset === 1 ? "amanhã" : ["no domingo", "na segunda", "na terça", "na quarta", "na quinta", "na sexta", "no sábado"][day]} às ${formatTime(m)}h`
    }
};

// Estado del negocio ahora: { open: true, label: "Abierto ahora", detail: "Cierra a las 23" }
// o { open: false, label: "Cerrado", detail: "Abre hoy a las 19" }. null si el horario
// no se entiende o el negocio no abre ningún día. `lang`: "es", "en" o "pt".
export const openStatus = (business, date = new Date(), lang = "es") => {
    const t = STATUS_TEXTS[lang] || STATUS_TEXTS.es;
    const week = parseSchedule(business?.horario, business?.horarioSec);
    if (!week || week.every((d) => !d.length)) return null;

    const { day, minutes } = argentinaClock(date);

    // Turnos de ayer a dentro de una semana, en minutos contados desde hoy a las 0.
    const shifts = [];
    for (let offset = -1; offset <= 7; offset++) {
        for (const [s, e] of week[(day + offset + 7) % 7]) {
            shifts.push([offset * DAY + s, offset * DAY + (e > s ? e : e + DAY)]);
        }
    }
    shifts.sort((a, b) => a[0] - b[0]);

    const current = shifts.find(([s, e]) => minutes >= s && minutes < e);
    if (current) {
        // Si otro turno arranca justo cuando termina este, cierra al final del último.
        let end = current[1];
        for (let more = true; more;) {
            more = false;
            for (const [s, e] of shifts) {
                if (s <= end && e > end) { end = e; more = true; }
            }
            if (end - minutes > 7 * DAY) break;
        }
        const detail = end - minutes >= DAY ? t.always : t.closes(end);
        return { open: true, label: t.open, detail };
    }

    const next = shifts.find(([s]) => s > minutes);
    const offset = Math.floor(next[0] / DAY);
    return { open: false, label: t.closed, detail: t.opens(offset, (day + offset) % 7, next[0]) };
}
